function debounceFn(fn, wait) {
  if (typeof debounce === 'function') return debounce(fn, wait);
  return fn;
}

function scrollGallerySlider(slider, slide) {
  if (!slider || !slide) return;

  slider.classList.add('is-programmatic-scroll');
  slider.style.setProperty('scroll-snap-type', 'none', 'important');
  slider.style.setProperty('scroll-behavior', 'auto', 'important');

  // Force a reflow so Safari/iOS actually drop snap before we set scrollLeft.
  void slider.offsetWidth;

  const dest = Math.round(
    slider.scrollLeft + slide.getBoundingClientRect().left - slider.getBoundingClientRect().left
  );

  if (typeof slider.scrollTo === 'function') {
    slider.scrollTo({ left: dest, behavior: 'auto' });
  }
  slider.scrollLeft = dest;

  window.clearTimeout(slider._restoreSnapTimer);
  slider._restoreSnapTimer = window.setTimeout(() => {
    slider.classList.remove('is-programmatic-scroll');
    slider.style.removeProperty('scroll-snap-type');
    slider.style.removeProperty('scroll-behavior');
  }, 350);
}

function activateGalleryMedia(gallery, mediaId) {
  if (!gallery || !mediaId) return false;

  const slider =
    gallery.querySelector('[id^="Slider-Gallery"]') || gallery.querySelector('.product__media-list');
  const slide = gallery.querySelector(`[data-media-id="${mediaId}"]`);
  if (!slider || !slide) return false;

  gallery.querySelectorAll('[data-media-id]').forEach((element) => {
    element.classList.toggle('is-active', element === slide);
  });

  gallery.querySelectorAll('[data-target]').forEach((item) => {
    const button = item.querySelector('button');
    if (!button) return;
    if (item.getAttribute('data-target') === mediaId) {
      button.setAttribute('aria-current', 'true');
    } else {
      button.removeAttribute('aria-current');
    }
  });

  scrollGallerySlider(slider, slide);
  return true;
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 771px)');
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounceFn(this.onSlideChanged.bind(this), 500));
        this.bindMobileSwipeSnap();

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;
            button.addEventListener('click', (event) => {
              event.preventDefault();
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          });
        }

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      bindMobileSwipeSnap() {
        const slider =
          (this.elements.viewer && this.elements.viewer.slider) ||
          this.querySelector('[id^="Slider-Gallery"]') ||
          this.querySelector('.product__media-list');
        if (!slider) return;

        let scrollTimer;
        const snapToNearest = () => {
          if (this.mql.matches) return;
          if (slider.classList.contains('is-programmatic-scroll') || slider.classList.contains('is-grabbing')) return;

          const slides = Array.from(slider.querySelectorAll('.product__media-item, .slider__slide')).filter(
            (slide) => slide.offsetParent !== null && slide.clientWidth > 0
          );
          if (!slides.length) return;

          const center = slider.scrollLeft + slider.clientWidth / 2;
          let nearest = slides[0];
          let nearestDist = Infinity;
          slides.forEach((slide) => {
            const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
            const dist = Math.abs(slideCenter - center);
            if (dist < nearestDist) {
              nearestDist = dist;
              nearest = slide;
            }
          });

          const dest = Math.round(nearest.offsetLeft);
          if (Math.abs(slider.scrollLeft - dest) > 2) {
            scrollGallerySlider(slider, nearest);
          }

          slides.forEach((slide) => slide.classList.toggle('is-active', slide === nearest));
          if (nearest.dataset.mediaId) {
            this.setActiveThumbnail(
              this.elements.thumbnails &&
                this.elements.thumbnails.querySelector(`[data-target="${nearest.dataset.mediaId}"]`)
            );
          }
        };

        slider.addEventListener(
          'scroll',
          () => {
            if (this.mql.matches) return;
            window.clearTimeout(scrollTimer);
            scrollTimer = window.setTimeout(snapToNearest, 80);
          },
          { passive: true }
        );

        slider.addEventListener('scrollend', snapToNearest, { passive: true });
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails || !event.detail || !event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const viewer = this.elements && this.elements.viewer;
        if (!viewer) return;

        const activeMedia = viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.scrollToMedia(activeMedia);
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      scrollToMedia(activeMedia) {
        const slider =
          (this.elements.viewer && this.elements.viewer.slider) ||
          this.querySelector('[id^="Slider-Gallery"]') ||
          (activeMedia && activeMedia.parentElement);
        scrollGallerySlider(slider, activeMedia);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
        });
        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', true);
        if (typeof this.elements.thumbnails.isSlideVisible !== 'function') return;
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    }
  );
}

if (!window.__productGalleryThumbNav) {
  window.__productGalleryThumbNav = true;
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('media-gallery button.thumbnail');
      if (!button) return;

      const thumbItem = button.closest('[data-target]');
      const gallery = button.closest('media-gallery');
      if (!thumbItem || !gallery) return;

      const mediaId = thumbItem.getAttribute('data-target');
      if (!mediaId) return;

      event.preventDefault();
      activateGalleryMedia(gallery, mediaId);

      if (typeof gallery.setActiveMedia === 'function' && gallery.elements) {
        gallery.setActiveMedia(mediaId, false);
      }
    },
    true
  );
}
